import { ILibraryDocument, ILibraryFolder, ILibraryListItem } from "@/api";
import {
    AttachmentType,
    IAttachmentOption,
} from "@/api/attachments/attachments.types";
import type { ChatSheetAttachment } from "@/components/assistant-ui/chat-sheet";

export const libraryFolderToListItem = (
    folder: ILibraryFolder
): ILibraryListItem => ({
    id: folder.id,
    name: folder.name,
    type: "folder",
    parent_id: folder.parent_id,
    is_knowledge_base: folder.is_knowledge_base,
    is_shared_root: folder.is_shared_root,
    is_shared: folder.is_shared,
    shared_by_organization_name: folder.shared_by_organization_name ?? null,
    shared_group_name: folder.shared_group_name ?? null,
    status: null,
    client_id: folder.client?.id ?? null,
    client_name: folder.client?.name ?? null,
    industry_name: null,
    carrier_name: null,
    clause_checklist_id: null,
    clause_checklist_name: null,
    item_count: 0,
    fields_count: null,
    library_folder_id: null,
    created_at: folder.created_at,
    updated_at: folder.updated_at,
});

export const libraryDocumentToListItem = (
    doc: ILibraryDocument
): ILibraryListItem => ({
    id: doc.id,
    name: doc.document_name,
    type: "document",
    parent_id: null,
    is_knowledge_base: null,
    is_shared_root: null,
    is_shared: null,
    shared_by_organization_name: doc.shared_by_organization_name ?? null,
    shared_group_name: doc.shared_group_name ?? null,
    status: null,
    client_id: doc.client?.id ?? null,
    client_name: doc.client?.name ?? null,
    industry_name: doc.industry?.name ?? null,
    carrier_name: doc.carrier?.carrier_name ?? null,
    clause_checklist_id: null,
    clause_checklist_name: null,
    item_count: 0,
    fields_count: null,
    library_folder_id: doc.library_folder_id,
    created_at: doc.created_at,
    updated_at: doc.updated_at,
});

const ATTACHMENT_TYPE_BY_LIBRARY_TYPE: Record<
    ILibraryListItem["type"],
    AttachmentType
> = {
    scope: AttachmentType.Scope,
    folder: AttachmentType.Folder,
    comparison: AttachmentType.Comparison,
    document: AttachmentType.Document,
};

interface LibraryItemMappingOptions {
    // Fallback for items that don't carry `is_knowledge_base` intrinsically
    // (only folders do). Reflects the page context — e.g. the active tab.
    isKnowledgeBaseDefault: boolean;
}

export const libraryItemToAttachmentOption = (
    item: ILibraryListItem,
    { isKnowledgeBaseDefault }: LibraryItemMappingOptions
): IAttachmentOption => ({
    attachment_id: item.id,
    attachment_type: ATTACHMENT_TYPE_BY_LIBRARY_TYPE[item.type],
    attachment_name: item.name,
    attachment_subtitle:
        item.type === "folder"
            ? `${item.item_count} ${item.item_count === 1 ? "item" : "items"}`
            : "",
    is_knowledge_base: item.is_knowledge_base ?? isKnowledgeBaseDefault,
    client_id: item.client_id,
    file_type: item.file_type,
});

export const libraryItemToChatSheetAttachment = (
    item: ILibraryListItem,
    { isKnowledgeBaseDefault }: LibraryItemMappingOptions
): ChatSheetAttachment => ({
    id: item.id,
    name: item.name,
    type: ATTACHMENT_TYPE_BY_LIBRARY_TYPE[item.type],
    is_knowledge_base: item.is_knowledge_base ?? isKnowledgeBaseDefault,
    file_type: item.file_type,
});
